/**
 * Overcut — fiche produit : barre d'achat collante, livraison estimée,
 * préchargement des autres pièces de la même voiture.
 *
 * Budget : chaque interaction doit répondre en moins de 150 ms. Donc aucun
 * écouteur de défilement (IntersectionObserver seulement), aucune requête
 * réseau sur le chemin d'un toucher, et des animations limitées à transform
 * et opacity. Le bouton de la barre ne refait pas l'ajout au panier : il
 * clique le vrai bouton du formulaire, qui garde toute la logique du thème
 * (variante, tiroir panier, erreurs).
 */
(() => {
  if (window.OvercutProduit) return;
  window.OvercutProduit = true;

  const reduit = matchMedia('(prefers-reduced-motion: reduce)');
  const plat = (el) => (el ? el.textContent.replace(/\s+/g, ' ').trim() : '');

  /* ---------- Barre d'achat collante (mobile) ---------- */

  class OvercutBarreProduit extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || document;
      this.bouton = this.querySelector('[data-barre-bouton]');
      this.libelle = this.querySelector('[data-barre-libelle]');
      this.prix = this.querySelector('[data-barre-prix]');
      this.choisi = this.dataset.variantes === '1';

      this.brancher();
      if (!this.vrai) return;

      // Visible quand le vrai bouton est sorti de l'écran par le haut,
      // masquée à l'approche du pied de page.
      let passe = false;
      let auPied = false;
      const maj = () => this.classList.toggle('is-visible', passe && !auPied);
      this.obsBouton = new IntersectionObserver(([e]) => {
        passe = !e.isIntersecting && e.boundingClientRect.top < 0;
        maj();
      });
      this.obsBouton.observe(this.vrai);
      const pied = document.querySelector('footer');
      if (pied) {
        this.obsPied = new IntersectionObserver(([e]) => {
          auPied = e.isIntersecting;
          maj();
        });
        this.obsPied.observe(pied);
      }

      // Une taille choisie à la main : la barre peut ajouter directement.
      this.surChoix = (e) => {
        if (e.target.closest && e.target.closest('variant-picker, [data-variant-picker], fieldset')) {
          this.choisi = true;
          this.rendre();
        }
      };
      this.section.addEventListener('change', this.surChoix);
      // Toucher la taille déjà présélectionnée ne déclenche aucun « change » :
      // le clic compte aussi comme un choix.
      this.section.addEventListener('click', this.surChoix);

      // Le thème remplace prix et bouton à chaque changement de variante.
      this.surVariante = () => requestAnimationFrame(() => {
        this.brancher();
        this.rendre();
      });
      document.addEventListener('variant:update', this.surVariante);

      this.bouton.addEventListener('click', () => this.agir());
      this.rendre();
    }

    disconnectedCallback() {
      this.obsBouton?.disconnect();
      this.obsPied?.disconnect();
      this.obsAttr?.disconnect();
      this.section?.removeEventListener('change', this.surChoix);
      this.section?.removeEventListener('click', this.surChoix);
      document.removeEventListener('variant:update', this.surVariante);
    }

    brancher() {
      const vrai = this.section.querySelector('product-form-component button[name="add"], product-form-component .add-to-cart-button');
      if (vrai === this.vrai) return;
      this.vrai = vrai;
      this.obsAttr?.disconnect();
      if (!vrai) return;
      // Rupture de stock : le vrai bouton se désactive, la barre suit.
      this.obsAttr = new MutationObserver(() => this.rendre());
      this.obsAttr.observe(vrai, { attributes: true, attributeFilter: ['disabled'] });
    }

    rendre() {
      if (!this.vrai) return;
      // Seul le prix payé : ni le prix barré ni les libellés pour lecteur d'écran.
      const prix = plat(
        this.section.querySelector('product-price .price__sale:not(.price__hidden) .price-item--sale') ||
          this.section.querySelector('product-price .price__regular:not(.price__hidden) .price') ||
          this.section.querySelector('product-price .price')
      );
      if (prix) this.prix.textContent = prix;
      // Pièce personnalisée : le module photo verrouille le vrai bouton tant que
      // l'aperçu n'est pas validé. La barre ne s'éteint pas pour autant : elle
      // ramène au module (« Envoyer ma photo »).
      this.perso = this.section.querySelector('.oc-perso');
      this.modePerso = Boolean(this.perso && this.vrai.disabled && this.dataset.perso);
      const indisponible = this.vrai.disabled && !this.modePerso;
      this.bouton.disabled = indisponible;
      if (this.modePerso) this.libelle.textContent = this.dataset.perso;
      else if (indisponible) this.libelle.textContent = plat(this.vrai) || this.dataset.epuise;
      else this.libelle.textContent = this.choisi ? this.dataset.ajouter : this.dataset.choisir;
    }

    agir() {
      if (this.modePerso) {
        this.perso.scrollIntoView({ behavior: reduit.matches ? 'auto' : 'smooth', block: 'center' });
        return;
      }
      if (!this.choisi) {
        // Pas de taille choisie : ajouter au hasard la première serait une
        // mauvaise surprise. On ramène au sélecteur, mis en évidence.
        const selecteur = this.section.querySelector('variant-picker, [data-variant-picker]');
        if (selecteur) {
          selecteur.scrollIntoView({ behavior: reduit.matches ? 'auto' : 'smooth', block: 'center' });
          selecteur.classList.remove('oc-attention');
          void selecteur.offsetWidth;
          selecteur.classList.add('oc-attention');
          return;
        }
      }
      this.vrai.click();
    }
  }

  customElements.define('overcut-barre-produit', OvercutBarreProduit);

  /* ---------- Livraison estimée et livraison offerte ---------- */

  const ouvres = (depart, jours) => {
    const d = new Date(depart);
    let reste = jours;
    while (reste > 0) {
      d.setDate(d.getDate() + 1);
      const j = d.getDay();
      if (j !== 0 && j !== 6) reste--;
    }
    return d;
  };

  class OvercutLivraison extends HTMLElement {
    connectedCallback() {
      const n = (k, def) => {
        const v = parseInt(this.dataset[k], 10);
        return Number.isFinite(v) ? v : def;
      };
      const fmt = new Intl.DateTimeFormat(document.documentElement.lang || 'fr', { weekday: 'long', day: 'numeric', month: 'long' });
      const court = new Intl.DateTimeFormat(document.documentElement.lang || 'fr', { weekday: 'short', day: 'numeric', month: 'short' });
      const maintenant = new Date();
      // Après l'heure limite, la commande part comme si elle était passée demain.
      const depart = maintenant.getHours() >= n('limite', 24) ? ouvres(maintenant, 1) : maintenant;
      const expMax = ouvres(depart, n('expMax', 2));
      const livMin = ouvres(depart, n('expMin', 1) + n('livMin', 2));
      const livMax = ouvres(depart, n('expMax', 2) + n('livMax', 3));

      const ecrire = (cle, texte) => {
        const el = this.querySelector(`[data-liv-${cle}]`);
        if (el) el.textContent = texte;
      };
      ecrire('expedie', court.format(expMax));
      ecrire('livre', `${court.format(livMin)} – ${court.format(livMax)}`);
      ecrire('phrase', this.dataset.phrase.replace('[date_min]', fmt.format(livMin)).replace('[date_max]', fmt.format(livMax)));
      this.classList.add('is-pret');

      this.seuil = n('seuil', 0);
      if (this.seuil > 0) {
        this.surPanier = (e) => {
          const total = e?.detail?.resource?.total_price;
          if (Number.isFinite(total)) this.jauge(total);
          else fetch('/cart.js', { headers: { Accept: 'application/json' } })
            .then((r) => r.json())
            .then((c) => this.jauge(c.total_price))
            .catch(() => {});
        };
        document.addEventListener('cart:update', this.surPanier);
        this.jauge(n('panier', 0));
      }
    }

    disconnectedCallback() {
      if (this.surPanier) document.removeEventListener('cart:update', this.surPanier);
    }

    jauge(total) {
      const texte = this.querySelector('[data-liv-offerte]');
      const barre = this.querySelector('[data-liv-jauge]');
      if (!texte || !barre) return;
      const reste = this.seuil - total;
      const p = Math.max(0, Math.min(1, total / this.seuil));
      // Une première pièce ajoutée fait déjà avancer la jauge : on montre le
      // trajet restant, pas un compteur à zéro.
      barre.style.transform = `scaleX(${Math.max(p, 0.04).toFixed(3)})`;
      if (reste <= 0) {
        texte.textContent = this.dataset.atteint;
        this.classList.add('is-offerte');
      } else {
        const montant = new Intl.NumberFormat(document.documentElement.lang || 'fr', { style: 'currency', currency: this.dataset.devise || 'EUR' }).format(reste / 100);
        texte.textContent = this.dataset.reste.replace('[montant]', montant);
        this.classList.remove('is-offerte');
      }
    }
  }

  customElements.define('overcut-livraison', OvercutLivraison);

  /* ---------- Préchargement au premier contact ---------- */

  // La navigation vers une autre pièce démarre dès que le doigt touche le
  // lien, avant même qu'il se lève : le changement de page paraît immédiat.
  const precharges = new Set();
  const precharger = (e) => {
    const a = e.target.closest && e.target.closest('a[data-oc-precharge]');
    if (!a || precharges.has(a.href)) return;
    precharges.add(a.href);
    const l = document.createElement('link');
    l.rel = 'prefetch';
    l.href = a.href;
    document.head.append(l);
  };
  document.addEventListener('pointerdown', precharger, { passive: true });
  document.addEventListener('mouseover', precharger, { passive: true });
})();
